import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { uk } from "date-fns/locale";
import {
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  WifiOff,
  RotateCw,
  Calendar,
  ListChecks,
  Clock,
  RefreshCw,
  UserSearch,
  ChevronDown,
  Users,
  Lock,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { ActionRow } from "@/components/home/queue/ActionRow";
import { CreateMenu } from "@/components/home/CreateMenu";
import { useHomeQueue } from "@/hooks/useHomeQueue";
import { useAuthStore } from "@/store/useAuthStore";
import { SupervisorHome, SupervisorHomeSkeleton } from "@/pages/supervisor/SupervisorHome";
import { useSupervisorSummary } from "@/hooks/useSupervisorSummary";
import { DirectorHome, DirectorHomeSkeleton } from "@/pages/director/DirectorHome";
import { useDirectorSummary } from "@/hooks/useDirectorSummary";
import { TaskDetailDrawer } from "@/components/task/TaskDetailDrawer";
import { TaskCreateDrawer } from "@/components/task/TaskCreateDrawer";
import { MeetingReportDrawer } from "@/components/client360/MeetingReportDrawer";
import { RenewalDrawer } from "@/components/contract/RenewalDrawer";
import type { HomeQueueWidgets, PriorityAction, QueueScope } from "@/api/types";

type DrawerKind = "task" | "report" | "renewal";

/** simple breakpoint hook — desktop shows 5 actions, mobile 3 (AC-F6-002). */
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return mobile;
}

const WIDGET_META: Record<
  keyof HomeQueueWidgets,
  { icon: LucideIcon; label: string; chip: string }
> = {
  todayMeetings: { icon: Calendar, label: "Зустрічі сьогодні", chip: "text-info-foreground" },
  overdueTasks: { icon: ListChecks, label: "Прострочені задачі", chip: "text-warning-foreground" },
  pendingReports: { icon: Clock, label: "Звіти не подано", chip: "text-warning-foreground" },
  contractRisks: { icon: RefreshCw, label: "Договори спливають", chip: "text-destructive" },
  complaintRisks: { icon: AlertTriangle, label: "Рекламації SLA", chip: "text-destructive" },
  // R7: manager fill-flow wording («на перевірку» = supervisor review queue — інше).
  profileReview: { icon: UserSearch, label: "Профілі, які треба доповнити", chip: "text-muted-foreground" },
};

const WIDGET_ORDER: Array<keyof HomeQueueWidgets> = [
  "todayMeetings",
  "overdueTasks",
  "pendingReports",
  "contractRisks",
  "complaintRisks",
  "profileReview",
];

/**
 * F6 Home Unified Action Queue — Daily Workspace.
 * Main column = backend-sorted priority queue (rendered in priorityRank order, NEVER sorted client-side).
 * Right rail = "Огляд дня" widgets (counters → filtered list) + lastUpdatedAt.
 */
/**
 * /today home. S2 (D-067): tier-gated surface. canViewSupervisor (backend flag,
 * never a role-name) is true for supervisor/director/admin — but only a SUPERVISOR
 * gets a `supervisorSummary` payload. The gate fetches it: data present →
 * SupervisorHome; absent (manager/director/admin/no-team) or error → personal home
 * (AC-SH-KPI-008 manager-no-team-UI · AC-SH-SCOPE-003 Director = separate S5).
 */
export function HomeQueuePage() {
  // S5 (C2): director / admin_data_all / super-admin → org-wide read-only Director
  // Home (fetch-driven gate; NOT manager-fallback). Then supervisor → team surface.
  const canViewDirector = useAuthStore((s) => !!s.permissions?.canViewDirector);
  const canViewSupervisor = useAuthStore((s) => !!s.permissions?.canViewSupervisor);
  if (canViewDirector) {
    return <DirectorHomeGate />;
  }
  if (!canViewSupervisor) {
    return <ManagerHomeQueue />;
  }
  return <SupervisorHomeGate />;
}

/**
 * S5 DirectorHomeGate (C2) — fetch-driven. directorSummary present → DirectorHome.
 * On error / null (defensive — director/admin always get a payload) → fall to the
 * supervisor gate (covers director-who-is-also-supervisor → their team surface),
 * which itself falls back to the personal home. NEVER a hard manager-fallback for
 * a director (AC-SH-SCOPE-003 — Director-surface preferred in P0).
 */
function DirectorHomeGate() {
  const { data, isPending, isError } = useDirectorSummary(true);
  if (isPending) return <DirectorHomeSkeleton />;
  if (isError || !data) return <SupervisorHomeGate />;
  return <DirectorHome summary={data} />;
}

function SupervisorHomeGate() {
  const { data, isPending, isError } = useSupervisorSummary(true);
  if (isPending) return <SupervisorHomeSkeleton />;
  if (isError || !data) return <ManagerHomeQueue />; // not a supervisor surface → own home
  return <SupervisorHome summary={data} />;
}

function ManagerHomeQueue() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data, isPending, isError, error, isFetching, refetch, scope, setScope, invalidate } = useHomeQueue();
  // Role-detection is NON-literal: supervisor = backend permission flag, never a
  // role-name string. Drives the Director roll-up «Скоро» CTA + team-scope notes.
  // (Manager has canViewSupervisor=false → no switch, no CTA → no regression.)
  const canViewSupervisor = useAuthStore((s) => !!s.permissions?.canViewSupervisor);
  const mePermissions = useAuthStore((s) => s.permissions);
  const isTeamScope = scope === "team";
  const [expanded, setExpanded] = useState(false);
  const [drawer, setDrawer] = useState<{ kind: DrawerKind; action: PriorityAction } | null>(null);
  // R7 — empty-state live CTA («+ Створити задачу» → canonical CreateTaskDrawer).
  const [emptyCreateOpen, setEmptyCreateOpen] = useState(false);

  // R4: backend-owned create-gating. home-summary payload wins; /me copy is the
  // fallback while the queue loads. Absent everywhere (flag off) → no menu.
  const allowedActions = data?.allowedActions ?? mePermissions?.allowedActions ?? null;
  // v1.2.2 rail blocks present ⇒ render the 4-tile + panels rail (else legacy).
  const f6Full = !!data?.todayMeetingsList || !!data?.myInternalRequests;

  // CTA: drawer targets open the F2-F5 drawer in-place; everything else
  // route-navigates (fallback for ContractDetail / Account360 anchors).
  const onAction = (a: PriorityAction) => {
    const target = a.cta.target;
    if (a.cta.targetType === "drawer" && a.cta.entityId) {
      if (target === "TaskDetailDrawer") return setDrawer({ kind: "task", action: a });
      if (target === "MeetingReportDrawer") return setDrawer({ kind: "report", action: a });
      if (target === "RenewalDrawer") return setDrawer({ kind: "renewal", action: a });
    }
    if (a.cta.route) navigate(a.cta.route);
  };

  // Errata 4: refresh the queue after a drawer action completes. Drawers don't
  // expose granular onComplete, so invalidate on close — covers complete/reopen/
  // postpone (task), report submitted (meeting), renew/submit (contract).
  const closeDrawer = () => {
    invalidate();
    setDrawer(null);
  };

  const actions = data?.priorityActions ?? [];
  const initialVisible = isMobile ? 3 : 5;
  const visible = expanded ? actions : actions.slice(0, initialVisible);
  const restCount = Math.max(0, actions.length - initialVisible);

  const lastUpdated = useMemo(() => {
    if (!data?.lastUpdatedAt) return null;
    try {
      return format(new Date(data.lastUpdatedAt), "d MMM HH:mm", { locale: uk });
    } catch {
      return null;
    }
  }, [data?.lastUpdatedAt]);

  const today = format(new Date(), "EEEE, d MMMM", { locale: uk });

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1320px] space-y-6">
        {/* Greeting (backend displayGreetingName — no concat) */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Вітаємо{data?.displayGreetingName ? `, ${data.displayGreetingName}` : ""} 👋
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5 capitalize">
              {today}
              {!isPending && !isError ? ` · ${actions.length} дій потребують уваги` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* «+ Створити» — backend allowedActions-gated (R4/D-031, §20 no dead CTA) */}
            {allowedActions && <CreateMenu allowedActions={allowedActions} onDone={invalidate} />}
            {/* Scope switch — ONLY from allowedScopes (never infer supervisor locally) */}
            {data && data.allowedScopes.length > 1 && (
              <div className="flex items-center gap-1 rounded-md border bg-background p-0.5">
                {(["my", "team"] as QueueScope[]).map((s) =>
                  data.allowedScopes.includes(s) ? (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setScope(s);
                        setExpanded(false);
                      }}
                      className={cn(
                        "px-3 py-1 rounded text-xs font-medium transition-colors",
                        scope === s
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {s === "my" ? "Мої" : "Команда"}
                    </button>
                  ) : null,
                )}
              </div>
            )}
            {/* Director roll-up — «Скоро» placeholder. Gated to supervisors
                (HOME-07). DISABLED, no /director route, no fake aggregate
                (HOME-03/04): backend caps at team scope, no director endpoint. */}
            {canViewSupervisor && (
              <button
                type="button"
                disabled
                title="Командний роллап (агрегати по командах) — скоро"
                className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-md border bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground"
              >
                <Lock className="h-3.5 w-3.5" />
                Роллап — скоро
              </button>
            )}
          </div>
        </div>

        {/* v1.2.2 — top counter tiles (4, per VISUAL LOCK; my-scope only — widgets are me-only) */}
        {f6Full && !isTeamScope && data && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {(
              [
                { icon: ListChecks, chip: "text-primary", count: data.kpis?.myTasks ?? null, label: "задачі", route: "/tasks" },
                { icon: AlertTriangle, chip: "text-destructive", count: data.widgets.overdueTasks?.count ?? null, label: "прострочено", route: data.widgets.overdueTasks?.route ?? "/tasks?bucket=overdue" },
                { icon: Calendar, chip: "text-info-foreground", count: data.widgets.todayMeetings?.count ?? null, label: "зустрічі", route: data.widgets.todayMeetings?.route ?? "/meetings?filter=today" },
                { icon: UserSearch, chip: "text-warning-foreground", count: data.widgets.profileReview?.count ?? null, label: "профілі", route: data.widgets.profileReview?.route ?? "/needs-review" },
              ] as const
            )
              .filter((t) => t.count !== null)
              .map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => navigate(t.route)}
                    className="flex items-center gap-2.5 rounded-lg border bg-card px-3 py-2.5 text-left transition-colors hover:border-primary"
                  >
                    <Icon className={cn("h-[18px] w-[18px] shrink-0", t.chip)} />
                    <span className="text-base font-bold tabular-nums">{t.count}</span>
                    <span className="text-xs text-muted-foreground">{t.label}</span>
                  </button>
                );
              })}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,840px)_340px] gap-6 justify-center">
          {/* MAIN — Unified Action Queue */}
          <div className="space-y-3 min-w-0">
            {/* stale notice */}
            {data?.stale && (
              <div className="flex items-center justify-between rounded-lg border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                <span>Дані можуть бути застарілі{lastUpdated ? ` · оновлено ${lastUpdated}` : ""}</span>
                <button
                  type="button"
                  onClick={() => void refetch()}
                  className="flex items-center gap-1 font-medium text-primary"
                >
                  <RotateCw className="h-3 w-3" /> Оновити
                </button>
              </div>
            )}
            {/* partial warning */}
            {data?.partial && (
              <div className="flex items-center gap-2 rounded-lg border border-warning-foreground/20 bg-warning px-3 py-2 text-xs text-warning-foreground">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                Частина даних недоступна ({data.missingSources.join(", ")}). Показано доступні дії.
              </div>
            )}

            {isPending ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex min-h-[72px] items-center gap-3 rounded-xl border bg-card p-3.5">
                    <div className="h-10 w-10 shrink-0 animate-pulse rounded-lg bg-muted" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
                      <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                    </div>
                    <div className="h-9 w-24 animate-pulse rounded-lg bg-muted" />
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="rounded-2xl border bg-card py-10 text-center">
                <WifiOff className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-semibold">Не вдалося завантажити дії</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {(error as Error)?.message || "Перевірте з'єднання та спробуйте ще раз."}
                </p>
                <Button onClick={() => void refetch()} className="mx-auto mt-4 gap-1.5">
                  <RotateCw className="h-4 w-4" /> Повторити
                </Button>
              </div>
            ) : actions.length === 0 ? (
              <div className="rounded-2xl border bg-card py-12 text-center">
                <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-primary" />
                <p className="text-[15px] font-semibold">На сьогодні немає дій 🎉</p>
                <p className="mt-1 text-sm text-muted-foreground">Немає прострочених дій. Гарної роботи!</p>
                {/* R7/§20: CRM-owned empty → LIVE CTA (не глуха заглушка) */}
                {allowedActions?.task && (
                  <Button onClick={() => setEmptyCreateOpen(true)} variant="outline" className="mx-auto mt-4 gap-1.5">
                    + Створити задачу
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <h2 className="font-semibold">Що зробити зараз</h2>
                  <span className="text-xs text-muted-foreground">
                    · пріоритет від системи{isTeamScope ? " · команда" : ""}
                  </span>
                </div>
                <div className="space-y-3">
                  {visible.map((a) => (
                    <ActionRow key={a.id} action={a} onAction={onAction} />
                  ))}
                </div>
                {restCount > 0 && !expanded && (
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="flex w-full items-center justify-center gap-1 rounded-lg py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    Ще {restCount} {restCount === 1 ? "дія" : "дій"}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                )}
              </>
            )}
          </div>

          {/* RIGHT RAIL — Огляд дня (secondary navigation) */}
          {!isError && (
            <aside className="space-y-3 xl:sticky xl:top-6 self-start">
              <div className="flex items-center gap-1.5">
                <h2 className="text-sm font-semibold text-muted-foreground">Огляд дня</h2>
                <span className="text-xs text-muted-foreground/70">
                  {isTeamScope ? "— команда" : "— навігація"}
                </span>
              </div>
              {isTeamScope ? (
                /* Team scope: backend `widgets` counters are ME-ONLY — showing
                   them under «Команда» would mislead as team totals (HOME-02).
                   Suppressed; team aggregates live in the future roll-up. */
                <div className="space-y-2">
                  <div className="rounded-lg border bg-card p-4">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Users className="h-4 w-4 text-primary" />
                      Командний роллап
                      <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        Скоро
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Зведення по командах, прострочені звіти та якість даних з’являться тут.
                      Поки що нижче — командна черга дій.
                    </p>
                  </div>
                  <p className="px-1 text-xs text-muted-foreground/80">
                    Лічильники «Огляд дня» — особисті; доступні у вкладці «Мої».
                  </p>
                </div>
              ) : f6Full && data ? (
                /* v1.2.2 rail — panel structure per VISUAL LOCK (Igor variant C:
                   panels 1-3 live; «Історія роботи» OMITTED — separate follow-up,
                   no fake/empty placeholder). Panels NAVIGATE only — never duplicate
                   a queue drawer-level CTA (ERRATA 3). */
                <div className="space-y-3">
                  {/* 1 — «Сьогодні — зустрічі» (3C, with times; me-only grain) */}
                  <div className="overflow-hidden rounded-lg border bg-card">
                    <div className="flex items-center justify-between border-b px-3.5 py-2.5">
                      <p className="flex items-center gap-1.5 text-sm font-semibold">
                        <Calendar className="h-4 w-4 text-primary" /> Сьогодні — зустрічі
                      </p>
                      {data.widgets.todayMeetings && (
                        <button
                          type="button"
                          onClick={() => navigate(data.widgets.todayMeetings!.route)}
                          className="text-xs font-medium text-primary"
                        >
                          Усі ›
                        </button>
                      )}
                    </div>
                    {(data.todayMeetingsList?.length ?? 0) === 0 ? (
                      <p className="px-3.5 py-3 text-xs text-muted-foreground">Сьогодні зустрічей немає.</p>
                    ) : (
                      (data.todayMeetingsList ?? []).map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => navigate(m.route)}
                          className="flex w-full items-center gap-2.5 border-b px-3.5 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50"
                        >
                          <span className="w-11 shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                            {m.timeLabel}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate">{m.title}</span>
                            {m.accountName && (
                              <span className="block truncate text-xs text-muted-foreground">{m.accountName}</span>
                            )}
                          </span>
                        </button>
                      ))
                    )}
                  </div>

                  {/* 2 — «Мої задачі» + «Мої запити у відділи» (R7 wording: я поставив, чекаю відділ) */}
                  <div className="overflow-hidden rounded-lg border bg-card">
                    <div className="flex items-center justify-between border-b px-3.5 py-2.5">
                      <p className="flex items-center gap-1.5 text-sm font-semibold">
                        <ListChecks className="h-4 w-4 text-primary" /> Мої задачі
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate("/tasks")}
                        className="text-xs font-medium text-primary"
                      >
                        Усі ›
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate("/tasks")}
                      className="flex w-full items-center justify-between border-b px-3.5 py-2.5 text-left text-sm hover:bg-muted/50"
                    >
                      <span className="text-muted-foreground">Мені потрібно зробити</span>
                      <span className="font-semibold tabular-nums">{data.kpis?.myTasks ?? 0}</span>
                    </button>
                    {data.myInternalRequests && (
                      <button
                        type="button"
                        onClick={() => navigate(data.myInternalRequests!.route)}
                        className="flex w-full items-center justify-between gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-muted/50"
                      >
                        <span className="min-w-0 text-muted-foreground">
                          Мої запити у відділи{" "}
                          <span className="text-[11px] text-muted-foreground/70">(я поставив · чекаю відділ)</span>
                        </span>
                        <span className="font-semibold tabular-nums">{data.myInternalRequests.count}</span>
                      </button>
                    )}
                  </div>

                  {/* 3 — «Профілі, які треба доповнити» (R7: manager fill-flow ≠ «на перевірку») */}
                  {data.widgets.profileReview && (
                    <div className="overflow-hidden rounded-lg border bg-card">
                      <div className="flex items-center justify-between border-b px-3.5 py-2.5">
                        <p className="flex items-center gap-1.5 text-sm font-semibold">
                          <UserSearch className="h-4 w-4 text-warning-foreground" /> Профілі, які треба
                          доповнити
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate(data.widgets.profileReview!.route)}
                          className="text-xs font-medium text-primary"
                        >
                          Черга ›
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(data.widgets.profileReview!.route)}
                        className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-sm hover:bg-muted/50"
                      >
                        <span className="text-muted-foreground">Потребують доповнення</span>
                        <span className="font-semibold tabular-nums text-warning-foreground">
                          {data.widgets.profileReview.count}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* legacy flat counters — flag home_f6_full OFF degradation (AC-H5) */
                <div className="space-y-2">
                  {data &&
                    WIDGET_ORDER.filter((k) => data.widgets[k]).map((k) => {
                      const w = data.widgets[k]!;
                      const meta = WIDGET_META[k];
                      const Icon = meta.icon;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => navigate(w.route)}
                          className="flex w-full items-center gap-3 rounded-lg border bg-card p-3 text-left transition-colors hover:border-primary"
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                            <Icon className={cn("h-[18px] w-[18px]", meta.chip)} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xl font-semibold leading-none tabular-nums">{w.count}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{meta.label}</p>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
              {lastUpdated && (
                <p className="text-xs text-muted-foreground/70">
                  Оновлено: {lastUpdated}
                  {isFetching ? " · оновлення…" : ""}
                </p>
              )}
            </aside>
          )}
        </div>
      </div>

      {/* CTA drawers (in-place; invalidate queue on close — Errata 4) */}
      {drawer?.kind === "task" && (
        <TaskDetailDrawer open onClose={closeDrawer} taskId={drawer.action.cta.entityId} />
      )}
      {/* D-055 S2: звіт по ЗУСТРІЧІ з Home — канонічний C+ wizard (debrief
          required ЗАВЖДИ, visual v0.2 порядок), НЕ F2 ReportInteractionDrawer
          (там summary зʼявлявся лише після чіпа — порушення C+; Igor STOP-S2).
          holdingId="" — кеш-інвалідація деградує до глобальних ключів
          (tasks/homeSummary), queue має власний refresh-on-close (Errata 4). */}
      {drawer?.kind === "report" && drawer.action.cta.entityId && (
        <MeetingReportDrawer
          target={{
            interactionId: drawer.action.cta.entityId,
            summary: drawer.action.accountName
              ? `${drawer.action.title} · ${drawer.action.accountName}`
              : drawer.action.title,
          }}
          holdingId=""
          onClose={closeDrawer}
        />
      )}
      {drawer?.kind === "renewal" && drawer.action.cta.entityId && (
        <RenewalDrawer
          open
          onClose={closeDrawer}
          contractId={drawer.action.cta.entityId}
          contractNumber={drawer.action.title.replace(/^Договір спливає:\s*/, "")}
        />
      )}
      {/* R7 empty-state live CTA → canonical TaskCreateDrawer (D-031) */}
      {emptyCreateOpen && (
        <TaskCreateDrawer
          open
          onClose={() => {
            invalidate();
            setEmptyCreateOpen(false);
          }}
        />
      )}
    </div>
  );
}
